import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { useAppDispatch } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';

import { DiscountStatusEnum, DiscountUsageTypeEnum, TransactionActionEnum } from '~utils/enums';
import UtilsFunctions from '~utils/functions';

import Text from '~components/atoms/Text';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import dayjs from 'dayjs';

import * as Styles from './_productManagementDiscountCreateForm.module.scss';
import { TDropdownOption } from '~types/components/labelledDropdown';

const ProductSettingDetailsFormValidation = (discountTypeOptions: TDropdownOption[], discountUsageTypeOptions: TDropdownOption[]) => Yup.object().shape({
    discountCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchZeroWhiteSpace().required('Discount Code is required'),
    discountName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matches(/^[^=+\-@]*$/, { message: 'Cannot include these characters (=, +, -, @)' })
        .required('Discount Name is required'),
    amount: Yup.number()
        .typeError('Value must be a number')
        .required('Amount is required')
        .test('no-exponential-notation',
            'Quantity must be a whole number (exponential notation is not allowed)',
            function (value) {
                const original = this.originalValue;
                if (original === undefined || original === null || original === '') return true;
                return !/e/i.test(String(original));
            }
        ),
    discountType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(discountTypeOptions.map(e => e.label) as string[]).label('Discount Type').required('Discount Type is required'),
    status: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(DiscountStatusEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Status').required('Status is required'),
    discountStartDate: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Discount Start Date').required('Discount Start Date is required'),
    discountEndDate: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .label('Discount End Date')
        .required('Discount End Date is required')
        .test(
            'is-after-start',
            'End date must be later than start date',
            function (value) {
                const { discountStartDate } = this.parent;

                if (!value || !discountStartDate) return true;

                return dayjs(value).isAfter(dayjs(discountStartDate));
            }
        ),
    usageType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(discountUsageTypeOptions.map(e => e.label) as string[]).label('Discount Usage Type').required('Discount Usage Type is required'),
});

const DiscountCreateForm = () => {
    const {
        Misc:
        {
            blockEnterKey,
        },
    } = UtilsFunctions;

    const dispatch = useAppDispatch();

    const [selectedDiscountType, setSelectedDiscountType] = useState(false);

    const statusTypeOptions = Object.values(DiscountStatusEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const discountTypeOptions = [
        {
            label: 'Markup (Bank Buy)',
            value: TransactionActionEnum[TransactionActionEnum['Bank Buy']],
        },
        {
            label: 'Discount (Bank Sell)',
            value: TransactionActionEnum[TransactionActionEnum['Bank Sell']],
        },
    ];

    const discountUsageTypeOptions = Object.values(DiscountUsageTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const ProductSettingDetailsFormInitialValues = {
        discountCode: '',
        discountName: '',
        amount: '',
        discountType: '',
        status: '',
        discountStartDate: '',
        discountEndDate: '',
        usageType: '',
    };

    const formik = useFormik({
        initialValues: ProductSettingDetailsFormInitialValues,
        validationSchema: ProductSettingDetailsFormValidation(discountTypeOptions, discountUsageTypeOptions),
        onSubmit: (values) => {
            const {
                discountCode,
                discountName,
                amount,
                discountType,
                status,
                discountStartDate,
                discountEndDate,
                usageType,
            } = values;

            dispatch(combinedActions.settingCreateDiscountAttempt({
                discountCode,
                discountName,
                amount: !selectedDiscountType ? Number(amount) : undefined,
                percentageAmount: selectedDiscountType ? Number(amount) : undefined,
                transactionType: TransactionActionEnum[discountType.replace(/.*\((.*)\).*/, '$1') as keyof typeof TransactionActionEnum],
                status: DiscountStatusEnum[status as keyof typeof DiscountStatusEnum],
                discountStartDate,
                discountEndDate,
                usageType: DiscountUsageTypeEnum[usageType as keyof typeof DiscountUsageTypeEnum],
            }));
        },
    });

    useEffect(() => {
        formik.resetForm();
    }, []);

    const renderDiscountDetails = () => {
        return (
            <form
                onSubmit={formik.handleSubmit}
                className={Styles['form-container']}
                onKeyDown={blockEnterKey}
            >
                <div className={Styles['form-subcontainer']}>
                    <LabelledInput
                        name='discountCode'
                        label='Discount Code: '
                        value={formik.values.discountCode || ''}
                        error={formik.touched.discountCode ? formik.errors.discountCode : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={Styles['label-no-margin']}
                    />

                    <LabelledInput
                        name='discountName'
                        label='Discount Name: '
                        value={formik.values.discountName || ''}
                        error={formik.touched.discountName ? formik.errors.discountName : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={Styles['label-no-margin']}
                    />

                    <div>
                        <Text
                            isBold
                            label='Amount: '
                            className={Styles.label}
                        />

                        <div className={Styles['flex-span-container']}>
                            <LabelledInput
                                name='amount'
                                label=''
                                value={formik.values.amount || ''}
                                error={formik.touched.amount ? formik.errors.amount : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={Styles['label-no-margin']}

                            />

                            <div className={Styles['flex-div']}>
                                <div>
                                    <input type='radio' id='html' name='fav_language' value='HTML' checked={!selectedDiscountType} onChange={() => setSelectedDiscountType(false)}/>
                                    <label htmlFor='html'>Fixed (RM)</label><br/>
                                </div>

                                <div>
                                    <input type='radio' id='css' name='fav_language' value='CSS' checked={selectedDiscountType} onChange={() => setSelectedDiscountType(true)}/>
                                    <label htmlFor='css'>Percentage (%)</label><br/>
                                </div>
                            </div>
                        </div>

                    </div>

                    <LabelledDropdown
                        id='discountType'
                        name='discountType'
                        label='Discount Type* :'
                        options={discountTypeOptions}
                        value={formik.values.discountType || ''}
                        error={formik.touched.discountType ? formik.errors.discountType : undefined}
                        onChange={(e) => formik.setFieldValue('discountType', e.target.value)}
                        // onBlur={formik.handleBlur}
                        autoComplete='off'
                        noOptionsMessage='No results found'
                        optionSelected={!!discountTypeOptions.find(option => option.value === formik.values.discountType.replace(/.*\((.*)\).*/, '$1'))}
                    />

                    <LabelledDropdown
                        id='status'
                        name='status'
                        label='Status* :'
                        options={statusTypeOptions}
                        value={formik.values.status || ''}
                        error={formik.touched.status ? formik.errors.status : undefined}
                        onChange={(e) => formik.setFieldValue('status', e.target.value)}
                        // onBlur={formik.handleBlur}
                        autoComplete='off'
                        noOptionsMessage='No results found'
                        optionSelected={!!DiscountStatusEnum[formik.values.status as keyof typeof DiscountStatusEnum]}
                    />

                    <LabelledDropdown
                        id='usageType'
                        name='usageType'
                        label='Discount Usage Type* :'
                        options={discountUsageTypeOptions}
                        value={formik.values.usageType || ''}
                        error={formik.touched.usageType ? formik.errors.usageType : undefined}
                        onChange={(e) => formik.setFieldValue('usageType', e.target.value)}
                        // onBlur={formik.handleBlur}
                        autoComplete='off'
                        noOptionsMessage='No results found'
                        optionSelected={!!discountUsageTypeOptions.find(option => option.value === formik.values.usageType)}
                    />

                    {/* <div></div> */}

                    <LabelledDatepicker
                        id='discountStartDate'
                        name='discountStartDate'
                        label='Discount Start Date:'
                        selectTime
                        selectedDate={formik.values.discountStartDate}
                        error={formik.touched.discountStartDate ? formik.errors.discountStartDate : undefined}
                        setSelectedDate={(date: string) => formik.setFieldValue('discountStartDate', dayjs(date).toISOString())}
                        onBlur={formik.handleBlur}
                        minDate={dayjs().toISOString()}
                    />

                    <LabelledDatepicker
                        id='discountEndDate'
                        name='discountEndDate'
                        label='Discount End Date*:'
                        selectTime
                        selectedDate={formik.values.discountEndDate}
                        error={formik.touched.discountEndDate ? formik.errors.discountEndDate : undefined}
                        setSelectedDate={(date: string) => formik.setFieldValue('discountEndDate', dayjs(date).toISOString())}
                        onBlur={formik.handleBlur}
                        minDate={formik.values.discountStartDate || dayjs().toISOString()}
                    />
                </div>

                <div className={Styles.container}>
                    <ActionButton
                        label='Create'
                        type='submit'
                        className={Styles['submit-button']}
                        disabled={!formik.dirty}
                    />
                </div>
            </form>
        );
    };

    return (
        <div className={Styles.container}>
            {renderDiscountDetails()}
        </div>
    );
};

export default DiscountCreateForm;
